import React, { useEffect, useRef } from "react";
import { useQuill } from "react-quilljs";
import "quill/dist/quill.snow.css";


export default function SummaryEditor({ initialValue = "", onChange }) {
  const isInitialMount = useRef(true);
  const lastPropValue = useRef(initialValue);
  const isUpdatingFromProp = useRef(false);

  const formatContentForEditor = (value) => {
    const rawValue = value || "";
    const hasHtmlTags = /<[^>]+>/.test(rawValue);

    if (hasHtmlTags) {
      return rawValue;
    }

    const escapedValue = rawValue
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

    // Support both real new lines and escaped "\n" text from API payloads.
    return escapedValue.replace(/\\n|\r\n|\r|\n/g, "<br>");
  };

  const modules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ["bold", "italic", "underline", "strike", "blockquote"],
      [
        { list: "ordered" },
        { list: "bullet" },
        { indent: "-1" },
        { indent: "+1" },
      ],
      ["link", "image"],
      ["clean"],
    ],
  };

  const { quill, quillRef } = useQuill({
    placeholder: "Paste or write your content here...",
    theme: "snow",
    modules,
  });

  // Set initial value when Quill is ready (first mount)
  useEffect(() => {
    if (quill && isInitialMount.current) {
      if (initialValue) {
        quill.clipboard.dangerouslyPasteHTML(formatContentForEditor(initialValue));
      }
      isInitialMount.current = false;
      lastPropValue.current = initialValue;
    }
  }, [quill, initialValue]);

  // Update content when initialValue prop changes (language switch)
  useEffect(() => {
    if (quill && !isInitialMount.current) {
      // Only update if the prop value actually changed
      if (lastPropValue.current !== initialValue) {
        const currentHtml = quill.root.innerHTML;
        const newValue = initialValue || "";

        // Normalize both for comparison (remove empty paragraphs that Quill adds)
        const normalizeHtml = (html) => html.replace(/<p><br><\/p>/g, '').replace(/<p>\s*<\/p>/g, '').trim();
        const normalizedCurrent = normalizeHtml(currentHtml);
        const normalizedNew = normalizeHtml(newValue);

        // Only update if content is actually different
        if (normalizedCurrent !== normalizedNew) {
          isUpdatingFromProp.current = true;
          quill.clipboard.dangerouslyPasteHTML(formatContentForEditor(newValue));
          // Reset flag after a brief delay to allow Quill to update
          setTimeout(() => {
            isUpdatingFromProp.current = false;
          }, 0);
        }
        lastPropValue.current = initialValue;
      }
    }
  }, [quill, initialValue]);

  // Set up text-change event listener
  useEffect(() => {
    if (quill) {
      const handleTextChange = () => {
        // Don't trigger onChange if we're updating from prop (language switch)
        if (!isUpdatingFromProp.current) {
          const html = quill.root.innerHTML;
          onChange?.(html);
        }
      };

      quill.on('text-change', handleTextChange);

      return () => {
        quill.off('text-change', handleTextChange);
      };
    }
  }, [quill, onChange]);


  return (
    // No `bg-white border rounded shadow p-3` here any more: that was a white
    // card drawn around the editor, which on a dark page framed it in a bright
    // band. The toolbar and the body carry their own background, border and
    // radius from theme.css, so the wrapper only has to be full width.
    <div className="w-100">
      <div
        ref={quillRef}
        aria-label="Summary Editor"
        style={{
          height: "250px",
          overflowY: "auto",
          lineHeight: "1.7",
          width: "100%",
        }}
      />
    </div>
  );

}